import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { VignetteStatus } from '../../types/member';
import { getVignetteStatusLabel, isVignetteSent } from '../../utils/vignette';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';

const Badge = styled.span<{ $status: VignetteStatus }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  width: fit-content;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  font-family: ${typography.fontFamily.primary};
  background: ${props => {
    switch (props.$status) {
      case VignetteStatus.SentByMail:
        return `${props.theme.colors.primary}18`;
      case VignetteStatus.ReceivedInPerson:
        return `${props.theme.colors.success}18`;
      default:
        return props.theme.colors.surfaceHover;
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case VignetteStatus.SentByMail:
        return props.theme.colors.primary;
      case VignetteStatus.ReceivedInPerson:
        return props.theme.colors.success;
      default:
        return props.theme.colors.textSecondary;
    }
  }};
  border: 1px solid
    ${props => {
      switch (props.$status) {
        case VignetteStatus.SentByMail:
          return `${props.theme.colors.primary}33`;
        case VignetteStatus.ReceivedInPerson:
          return `${props.theme.colors.success}33`;
        default:
          return props.theme.colors.border;
      }
    }};
`;

const Dot = styled.span<{ $status: VignetteStatus }>`
  width: 8px;
  height: 8px;
  border-radius: ${borders.radius.full};
  background: currentColor;
  opacity: ${props => (isVignetteSent(props.$status) ? 1 : 0.55)};
`;

interface VignetteStatusBadgeProps {
  status: VignetteStatus;
  className?: string;
}

export const VignetteStatusBadge: React.FC<VignetteStatusBadgeProps> = ({
  status,
  className,
}) => {
  const { t } = useTranslation();
  return (
    <Badge $status={status} className={className}>
      <Dot $status={status} aria-hidden />
      {getVignetteStatusLabel(status, t)}
    </Badge>
  );
};

export default VignetteStatusBadge;
